import { FC, ReactElement } from 'react';

import { Fade } from '@mui/material';

import { DEFERRED_LOADING_FADE_MS } from 'hooks';
import { prefersReducedMotion, shouldSlide } from 'utils/pageTransition';

/**
 * The second half of the platform's page transition: the loaded content fading in
 * over the footprint its skeleton just reserved.
 *
 * `apps/platform` writes this inline as `<Fade in timeout={DEFERRED_LOADING_FADE_MS}>`
 * around a page's own root (`SupportRequestPage`, `WhatsNewAnnouncementPage`,
 * `SupportPage`, `WhatsNewPage`, `InternalSupportRequestPage` — five call sites). This
 * app needs it at five sites too, and needs one thing the platform does not, so it is a
 * component rather than a repeated literal (§36 — the second hand-roll is a shared
 * piece).
 *
 * ## The thing the platform does not need: DO NOT FADE ON MOBILE
 *
 * The platform is a desktop console and has exactly one page transition. This app has
 * two, split by width — below 900px (`shouldSlide()`) a navigation is a horizontal
 * PUSH, which is a phone idiom the platform has no equivalent of and which is not
 * being changed here.
 *
 * Running both would change the phone: the layout wrapper translates the incoming page
 * across 260ms while this faded its contents up over the first 120ms of that travel, so
 * the page would arrive partly transparent. That is a visible regression on the surface
 * that matters most — the app is reached by tapping an NFC tag with a phone, so 375 is
 * the primary viewport.
 *
 * So below 900px this renders the child **untouched, with no `Fade` in the tree at
 * all** — not a `Fade` with `timeout={0}`, which would still mount the element at
 * `opacity: 0` for a frame and reintroduce exactly the flash being removed. The mobile
 * render path is byte-for-byte what it was.
 *
 * ## Width is read at render, not subscribed to
 *
 * Matching `shouldSlide()`, which every other call site in this app already reads the
 * same way. A viewport crossing 900px mid-session is not a case worth a resize listener
 * here: the consequence is one navigation getting the other treatment, and the next
 * render corrects it.
 *
 * ## Opacity only
 *
 * No transform, ever. A transform on a page root becomes the containing block for every
 * `position: fixed` descendant, and `StickyActionBar` is one — it would jump sideways by
 * the page's centring offset for the duration. That is the same constraint that makes
 * `NavigationHeader` `sticky` rather than `fixed`, and it is why MUI's `Fade` (opacity,
 * and it clones its child rather than wrapping it in a div) is the right primitive.
 */
export const ContentFade: FC<{ children: ReactElement }> = ({ children }) => {
  /*
   * Reduced motion degrades to an INSTANT swap, same as the slide.
   *
   * This has to be handled here rather than left to CSS. The deleted `fadeInSx` was a
   * CSS `animation`, so it could carry its own `@media (prefers-reduced-motion: reduce)
   * { animation: none }` block; MUI's `Fade` writes an INLINE `transition` on the
   * element, and an inline style cannot be overridden by a media query. So the only
   * place to honour the preference is by not mounting the `Fade` at all.
   *
   * Read at render, like `shouldSlide()` — see the note below on why that is enough.
   */
  if (prefersReducedMotion()) return children;

  if (shouldSlide()) return children;

  return (
    <Fade in timeout={DEFERRED_LOADING_FADE_MS}>
      {children}
    </Fade>
  );
};
